import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar, MapPin, Pencil, Plus } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import type { Event, Organization } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import { useYetki } from '@/lib/yetki';
import { formatDate } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { InviteLinkBox } from '@/components/ui/InviteLinkBox';
import { Badge, Button, Card, Input, Label, Spinner } from '@/components/ui';

export function EventsPage() {
  const { effectiveIsSuperAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('org');
  const [showCreate, setShowCreate] = useState(false);
  const [expandedInvite, setExpandedInvite] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', startDate: '', endDate: '', locationName: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { data: orgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api<{ organizations: Organization[] }>('/organizations'),
  });

  const selectedOrg = orgId ?? orgs?.organizations[0]?.id;
  const { yonetebilir } = useYetki(selectedOrg);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['events', selectedOrg],
    queryFn: () => api<{ events: Event[] }>(`/events/org/${selectedOrg}`),
    enabled: !!selectedOrg,
  });

  async function createEvent(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!selectedOrg) {
      setError('Önce bir organizasyon oluşturmalısınız.');
      return;
    }
    setLoading(true);
    try {
      await api(`/events/org/${selectedOrg}`, {
        method: 'POST',
        body: JSON.stringify({ ...form, status: 'published' }),
      });
      setShowCreate(false);
      setForm({ name: '', startDate: '', endDate: '', locationName: '' });
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Program oluşturulamadı');
    } finally {
      setLoading(false);
    }
  }

  /* Eskiden "organizasyonu varsa yönetebilir" sayılıyordu; katılımcılar
     da program ekleme ve düzenleme düğmelerini görüyordu. */
  const canManageEvents = yonetebilir && Boolean(selectedOrg);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Programlar"
        subtitle="Organizasyon içindeki etkinlikler (Barla 2026, Çamdağı vb.)"
        backTo="/dashboard"
      />

      {!selectedOrg && (
        <Card ornate={false} className="border-amber-200 bg-amber-50/80">
          <p className="text-sm text-amber-950">Önce ana sayfadan organizasyona katılın veya oluşturun.</p>
          <Link to="/dashboard" className="mt-2 inline-block text-sm font-medium text-primary underline">
            Ana Sayfa
          </Link>
        </Card>
      )}

      <div className="flex justify-end">
        {canManageEvents && (
          <Button size="sm" variant="gold" onClick={() => setShowCreate(!showCreate)}>
            <Plus className="h-4 w-4" />
            Yeni Program Ekle
          </Button>
        )}
      </div>

      {orgs && orgs.organizations.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {orgs.organizations.map((org) => (
            <Link key={org.id} to={`/events?org=${org.id}`}>
              <Badge variant={org.id === selectedOrg ? 'gold' : 'default'}>{org.name}</Badge>
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <Card>
          <form onSubmit={createEvent} className="space-y-3">
            <Input
              placeholder="Program adı (ör. Barla 2026)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              minLength={2}
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Başlangıç</Label>
                <Input
                  type="date"
                  value={form.startDate}
                  max={form.endDate || undefined}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Bitiş</Label>
                <Input
                  type="date"
                  value={form.endDate}
                  min={form.startDate || undefined}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                />
              </div>
            </div>
            <Input
              placeholder="Konum (Barla / Çamdağı)"
              value={form.locationName}
              onChange={(e) => setForm({ ...form, locationName: e.target.value })}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" variant="gold" disabled={loading}>
              {loading ? <Spinner /> : 'Ekle'}
            </Button>
          </form>
        </Card>
      )}

      {/* Yeni program formu açıkken liste gizleniyor: ekranda aynı anda
          hem doldurulacak alanlar hem mevcut programlar durunca hangisine
          bakılacağı belirsizdi. */}
      {showCreate ? null : isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner className="h-8 w-8" />
        </div>
      ) : !selectedOrg ? null : (data?.events ?? []).length === 0 ? (
        <Card className="text-center">
          <p className="text-muted-foreground">Henüz program yok veya erişim yetkiniz yok</p>
        </Card>
      ) : (
        (data?.events ?? []).map((event) => (
          <Card key={event.id}>
            <Link to={`/event/${event.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-serif text-lg font-semibold text-primary">{event.name}</h3>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {event.startDate && (
                      <p className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(event.startDate)}
                        {event.endDate && event.endDate !== event.startDate && ` – ${formatDate(event.endDate)}`}
                      </p>
                    )}
                    {event.locationName && (
                      <p className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {event.locationName}
                      </p>
                    )}
                  </div>
                </div>
                <Badge variant={event.status === 'published' ? 'success' : 'default'}>
                  {event.status === 'published' ? 'Yayında' : 'Taslak'}
                </Badge>
              </div>
            </Link>

            {/* Davet linki yönetici işi: katılımcı eline geçirip
                organizasyona istediğini çağırabiliyordu. */}
            {!canManageEvents ? null : expandedInvite === event.id ? (
              <div className="mt-3">
                <InviteLinkBox
                  inviteCode={event.inviteCode}
                  label="Program Davet Linki"
                  onRegenerate={
                    effectiveIsSuperAdmin
                      ? async () => {
                          const res = await api<{ inviteCode: string }>(
                            `/events/${event.id}/regenerate-invite`,
                            { method: 'POST' },
                          );
                          await refetch();
                          return res.inviteCode;
                        }
                      : undefined
                  }
                />
              </div>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="mt-2 w-full text-xs"
                onClick={() => setExpandedInvite(expandedInvite === event.id ? null : event.id)}
              >
                Davet Linkini Göster
              </Button>
            )}

            <div className="mt-3 flex gap-2">
              <Link to={`/event/${event.id}`} className="flex-1">
                <Button variant="outline" className="w-full" size="sm">
                  Program Akışı
                </Button>
              </Link>
              {canManageEvents && (
                <Link to={`/event/${event.id}/edit`} className="flex-1">
                  <Button variant="outline" className="w-full" size="sm">
                    <Pencil className="h-3 w-3" />
                    Düzenle
                  </Button>
                </Link>
              )}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
